import React, { useState, useEffect } from 'react';
import { Search, Mail, Phone, Building, Coffee, DollarSign, FileText, UserCheck, Star, BookMarked } from 'lucide-react';
import { meetingService } from '../utils/supabaseData';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import { restaurantFavoritesService } from '../utils/restaurantFavoritesService';
import { savedOrdersService } from '../utils/savedOrdersService';
import { logger } from '../utils/logger';
import SavedOrderPopup from '../components/SavedOrderPopup';
import EnhancedMeetingCard from '../components/EnhancedMeetingCard';

export default function MeetingHistoryView({ currentUser }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvedReps, setApprovedReps] = useState([]);
  const [actionStatus, setActionStatus] = useState('');

  // Saved order popup state
  const [showSavedOrderPopup, setShowSavedOrderPopup] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // Load meetings from Supabase
  useEffect(() => {
    const loadMeetings = async () => {
      if (!currentUser?.id) return;

      try {
        setLoading(true);
        const allMeetings = await meetingService.getAllMeetings(currentUser.id, currentUser.user_type);

        // Filter for past meetings (yesterday and earlier)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const pastMeetings = allMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.meeting_date || meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate < today;
        });

        // Transform meetings to match Dashboard format (same as Dashboard.js)
        const enrichedMeetings = await Promise.all(pastMeetings.map(async meeting => {
          let salesRepName = 'Sales Representative';
          let salesRepProfilePicture = null;
          let salesRepEmail = 'contact@company.com';
          let salesRepPhone = '(555) 000-0000';
          let salesRepCompany = 'Company';
          let salesRepTerritory = 'Territory';
          let salesRepProducts = ['Products'];

          // Fetch sales rep details if we have a sales_rep_id and user is a customer
          if (meeting.sales_rep_id && currentUser.user_type === 'customer') {
            try {
              const { supabase } = await import('../utils/supabase');
              const { data: salesRepData, error } = await supabase
                .from('user_profiles')
                .select('first_name, last_name, email, phone, company, territory, products, profile_picture')
                .eq('id', meeting.sales_rep_id)
                .single();

              if (error) {
                logger.warn('Meeting History: Sales rep query error', { error: error.message, salesRepId: meeting.sales_rep_id });
              } else if (salesRepData) {
                // Transform name (same logic as Dashboard)
                if (salesRepData.first_name || salesRepData.last_name) {
                  salesRepName = `${salesRepData.first_name || ''} ${salesRepData.last_name || ''}`.trim();
                } else if (salesRepData.email) {
                  salesRepName = salesRepData.email.split('@')[0];
                } else {
                  salesRepName = 'Sales Representative';
                }
                salesRepProfilePicture = salesRepData.profile_picture;
                salesRepEmail = salesRepData.email || 'contact@company.com';
                salesRepPhone = salesRepData.phone || '(555) 000-0000';
                salesRepCompany = salesRepData.company || 'Company';
                salesRepTerritory = salesRepData.territory || 'Territory';
                salesRepProducts = salesRepData.products || ['Products'];
              }
            } catch (fetchError) {
              logger.warn('Meeting History: Sales rep fetch exception', { error: fetchError.message });
            }
          } else if (meeting.external_sales_rep_name && currentUser.user_type === 'customer') {
            // External sales rep (manually added meeting)
            salesRepName = meeting.external_sales_rep_name;
            salesRepEmail = meeting.external_sales_rep_email || 'contact@company.com';
            salesRepPhone = meeting.external_sales_rep_phone || '(555) 000-0000';
            salesRepCompany = meeting.external_sales_rep_company || 'Company';
          }

          // Return transformed meeting (matching Dashboard format + repInfo for buttons)
          return {
            ...meeting,
            // Transform to match Dashboard/EnhancedMeetingCard expectations
            date: meeting.meeting_date || meeting.date,
            time: meeting.start_time || meeting.time,
            salesRep: salesRepName,
            sales_rep_profile_picture: salesRepProfilePicture,
            restaurant: meeting.restaurant_name || meeting.restaurant || 'Restaurant',
            status: meeting.status || 'completed',
            // Add repInfo structure for button functionality
            repInfo: {
              name: salesRepName,
              email: salesRepEmail,
              phone: salesRepPhone,
              company: salesRepCompany,
              territory: salesRepTerritory,
              products: salesRepProducts,
              title: 'Sales Representative',
              profilePicture: salesRepProfilePicture
            }
          };
        }));

        setMeetings(enrichedMeetings);
      } catch (error) {
        logger.error('Error loading meetings:', error);
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };

    loadMeetings();
  }, [currentUser]);

  // Load approved reps for customers
  useEffect(() => {
    const loadApprovedReps = async () => {
      if (!currentUser?.id || currentUser.user_type !== 'customer') return;

      try {
        const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
        setApprovedReps(reps.map(rep => ({
          id: rep.salesRepId,
          name: rep.salesRepName,
          email: rep.salesRepEmail
        })));
      } catch (error) {
        logger.error('Error loading approved reps:', error);
      }
    };

    loadApprovedReps();
  }, [currentUser]);

  // Check if a rep is already approved
  const isRepApproved = (salesRepId) => {
    return approvedReps.some(rep => rep.id === salesRepId);
  };

  // Add rep to approved list
  const handleAddToApprovedList = async (meeting) => {
    try {
      const salesRepId = meeting.sales_rep_id;
      if (!salesRepId) {
        setActionStatus('Unable to identify sales rep');
        setTimeout(() => setActionStatus(''), 3000);
        return;
      }

      const result = await preApprovedRepsService.addPreApprovedRep(
        currentUser.id,
        salesRepId,
        null
      );

      if (result) {
        setActionStatus(`${meeting.repInfo.name} added to approved list`);
        // Reload approved reps
        const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
        setApprovedReps(reps.map(rep => ({
          id: rep.salesRepId,
          name: rep.salesRepName,
          email: rep.salesRepEmail
        })));
      } else {
        setActionStatus('Could not add to approved list');
      }
    } catch (error) {
      logger.error('Error adding to approved list:', error);
      setActionStatus('Error adding to approved list');
    }
    setTimeout(() => setActionStatus(''), 3000);
  };

  // Add restaurant to favorites
  const handleAddToFavorites = async (meeting) => {
    try {
      if (!meeting.restaurant_id) {
        setActionStatus('Restaurant information not available');
        setTimeout(() => setActionStatus(''), 3000);
        return;
      }

      await restaurantFavoritesService.addFavoriteRestaurant(
        currentUser.id,
        meeting.restaurant_id,
        null
      );

      setActionStatus(`${meeting.restaurant_name || meeting.restaurant} added to favorites`);
    } catch (error) {
      logger.error('Error adding restaurant to favorites:', error);
      if (error.message.includes('already in your favorites')) {
        setActionStatus('Restaurant already in favorites');
      } else {
        setActionStatus('Error adding to favorites');
      }
    }
    setTimeout(() => setActionStatus(''), 3000);
  };

  // Save order from this meeting
  const handleSaveOrder = (meeting) => {
    if (!meeting.restaurant_name) {
      setActionStatus('Restaurant information not available');
      setTimeout(() => setActionStatus(''), 3000);
      return;
    }

    // Build restaurant object for popup
    const restaurant = {
      name: meeting.restaurant_name || meeting.restaurant,
      address: meeting.restaurant_address || null,
      phone: meeting.restaurant_phone || null,
      yelp_business_id: meeting.restaurant_yelp_id || null,
      ...meeting.restaurant_data
    };

    setSelectedRestaurant(restaurant);
    setShowSavedOrderPopup(true);
  };

  // Filter meetings based on search and time period (data already transformed in useEffect)
  const filteredMeetings = meetings.filter(meeting => {
    const repName = (meeting.repInfo?.name || '').toString();
    const repCompany = (meeting.repInfo?.company || '').toString();
    const repProducts = Array.isArray(meeting.repInfo?.products) ? meeting.repInfo.products : [];

    const matchesSearch = repName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         repCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         repProducts.some(product =>
                           (product || '').toString().toLowerCase().includes(searchTerm.toLowerCase())
                         );

    if (!matchesSearch) return false;

    if (filterPeriod === 'all') return true;
    
    const meetingDate = new Date(meeting.date);
    const now = new Date();
    
    switch (filterPeriod) {
      case 'week':
        return (now - meetingDate) <= 7 * 24 * 60 * 60 * 1000;
      case 'month':
        return (now - meetingDate) <= 30 * 24 * 60 * 60 * 1000;
      case 'quarter':
        return (now - meetingDate) <= 90 * 24 * 60 * 60 * 1000;
      default:
        return true;
    }
  });

  const sortedMeetings = filteredMeetings.sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Meeting History</h2>

      {/* Status Message */}
      {/* Action Status Message */}
      {actionStatus && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {actionStatus}
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by rep name, company, or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last 3 Months</option>
          </select>
        </div>
      </div>

      {/* Meeting History List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Loading meeting history...</p>
        </div>
      ) : sortedMeetings.length > 0 ? (
        <div className="space-y-4">
          {sortedMeetings.map(meeting => (
            <div key={meeting.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
              {/* Enhanced Meeting Card - same as dashboard */}
              <div onClick={(e) => e.preventDefault()}>
                <EnhancedMeetingCard
                  meeting={meeting}
                  onClick={() => {}} // No navigation action
                  userType={currentUser?.user_type}
                  showDetails={false}
                  compact={false}
                />
              </div>

              {/* Sales Rep Details & Action Buttons Section */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                {/* Sales Rep Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Contact Information */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Contact Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${meeting.repInfo.email}`} className="text-blue-600 hover:underline truncate">
                          {meeting.repInfo.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${meeting.repInfo.phone?.replace(/\D/g, '')}`} className="text-blue-600 hover:underline">
                          {meeting.repInfo.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700">{meeting.repInfo.company}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Coffee className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700">{meeting.repInfo.territory}</span>
                      </div>
                    </div>
                  </div>

                  {/* Products Represented */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Products Represented</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {meeting.repInfo.products.map((product, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap pt-3 border-t border-gray-200">
                  <a
                    href={`mailto:${meeting.repInfo.email}?subject=Follow up on our lunch meeting`}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Send Email
                  </a>
                  <a
                    href={`tel:${meeting.repInfo.phone?.replace(/\D/g, '')}`}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Call
                  </a>
                  {currentUser?.user_type === 'customer' && !isRepApproved(meeting.sales_rep_id) && (
                    <button
                      onClick={() => handleAddToApprovedList(meeting)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <UserCheck className="w-4 h-4" />
                      Add to Approved List
                    </button>
                  )}
                  {currentUser?.user_type === 'customer' && isRepApproved(meeting.sales_rep_id) && (
                    <div className="px-4 py-2 bg-purple-100 text-purple-700 text-sm rounded-lg flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Pre-Approved
                    </div>
                  )}
                  {currentUser?.user_type === 'customer' && (
                    <button
                      onClick={() => handleAddToFavorites(meeting)}
                      className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Star className="w-4 h-4" />
                      Add to Favorites
                    </button>
                  )}
                  {currentUser?.user_type === 'customer' && (
                    <button
                      onClick={() => handleSaveOrder(meeting)}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <BookMarked className="w-4 h-4" />
                      Save Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {searchTerm ? 'No meetings found matching your search.' : 'No past meetings yet.'}
          </p>
          {searchTerm ? (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-blue-600 hover:underline text-sm"
            >
              Clear search
            </button>
          ) : (
            <p className="text-sm text-gray-500 mt-2">
              Past meetings will appear here after your scheduled meetings have occurred.
            </p>
          )}
        </div>
      )}

      {/* Summary Stats */}
      {sortedMeetings.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-600 mb-1">Total Meetings</p>
            <p className="text-2xl font-bold text-blue-900">{sortedMeetings.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-600 mb-1">Unique Representatives</p>
            <p className="text-2xl font-bold text-green-900">
              {new Set(sortedMeetings.map(m => m.repInfo.name)).size}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-600 mb-1">Companies</p>
            <p className="text-2xl font-bold text-purple-900">
              {new Set(sortedMeetings.map(m => m.repInfo.company)).size}
            </p>
          </div>
        </div>
      )}

      {/* Saved Order Popup */}
      {showSavedOrderPopup && selectedRestaurant && (
        <SavedOrderPopup
          isOpen={showSavedOrderPopup}
          onClose={() => {
            setShowSavedOrderPopup(false);
            setSelectedRestaurant(null);
          }}
          restaurant={selectedRestaurant}
          customerId={currentUser?.id}
        />
      )}
    </div>
  );
}